/**
 * Color Lookup Table (LUT) Service
 * Uses Cornerstone3D native viewport.setProperties({ colormap }) API
 *
 * Cornerstone3D lookup chain:
 *   1. Custom registry (utilities.colormap.registerColormap)
 *   2. VTK.js built-in presets (vtkColorMaps.getPresetByName)
 *
 * The colormap object passed to setProperties follows ColormapPublic interface:
 *   { name: string, opacity?: number | OpacityMapping[] }
 *
 * PET/CT Fusion:
 *   Overlays a PET series onto the active CT viewport as a colorized actor.
 *   Uses the heatmap overlay pattern (createActorMapper + addActor) with
 *   VTK.js color transfer functions for LUT-based colorization.
 */

import { Enums, utilities } from "@cornerstonejs/core";
import { loadAndCacheImage } from "@cornerstonejs/core/loaders/imageLoader";
import vtkColorTransferFunction from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction";
import vtkColorMaps from "@kitware/vtk.js/Rendering/Core/ColorTransferFunction/ColorMaps";

// ─── Custom colormap definitions ───────────────────────────────────────────────
// Medical imaging and MATLAB-style palettes not available as VTK.js built-ins.
// Registered at module load so they work in both viewport.setProperties() and
// the vtkColorTransferFunction fallback inside applyPETFusion().
//
// RGBPoints format: flat array [x, r, g, b, x, r, g, b, …] where x and r/g/b are all 0-1.
const _CUSTOM_COLORMAP_DEFS = [
  {
    Name: "Hot Iron",
    ColorSpace: "RGB",
    // Black → dark-red → red → orange → yellow → white
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.333, 0.502, 0.000, 0.000,
      0.500, 1.000, 0.000, 0.000,
      0.667, 1.000, 0.502, 0.000,
      0.833, 1.000, 1.000, 0.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "PET",
    ColorSpace: "RGB",
    // Black → blue → cyan → green → yellow → orange → red → white
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.100, 0.000, 0.000, 0.400,
      0.200, 0.000, 0.000, 0.800,
      0.300, 0.000, 0.400, 0.800,
      0.400, 0.000, 0.800, 0.400,
      0.500, 0.000, 0.800, 0.000,
      0.600, 0.800, 0.800, 0.000,
      0.700, 1.000, 0.600, 0.000,
      0.800, 1.000, 0.200, 0.000,
      0.900, 0.900, 0.000, 0.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "Hot Metal Blue",
    ColorSpace: "RGB",
    // Black → dark-blue → purple → red → orange → yellow
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.200, 0.000, 0.000, 0.502,
      0.400, 0.502, 0.000, 0.502,
      0.600, 1.000, 0.000, 0.000,
      0.800, 1.000, 0.502, 0.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "PET 20 Step",
    ColorSpace: "RGB",
    // 20 visually distinct bands — each control point marks the centre of one band
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.050, 0.000, 0.000, 0.400,
      0.100, 0.000, 0.000, 0.800,
      0.150, 0.000, 0.400, 1.000,
      0.200, 0.000, 0.800, 1.000,
      0.250, 0.000, 0.800, 0.600,
      0.300, 0.000, 0.800, 0.000,
      0.350, 0.400, 1.000, 0.000,
      0.400, 0.800, 1.000, 0.000,
      0.450, 1.000, 1.000, 0.000,
      0.500, 1.000, 0.800, 0.000,
      0.550, 1.000, 0.600, 0.000,
      0.600, 1.000, 0.400, 0.000,
      0.650, 1.000, 0.200, 0.000,
      0.700, 1.000, 0.000, 0.000,
      0.750, 0.800, 0.000, 0.000,
      0.800, 0.600, 0.000, 0.200,
      0.850, 0.800, 0.400, 0.600,
      0.900, 1.000, 0.700, 0.800,
      0.950, 1.000, 1.000, 1.000,
      1.000, 1.000, 1.000, 1.000,
    ],
  },
  {
    Name: "Spring",
    ColorSpace: "RGB",
    // Black (background stays unlit, matches Hot Iron/PET/Hot Metal Blue) → Magenta → Yellow (MATLAB spring)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 1.000, 0.000, 1.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "Summer",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Green-teal → Yellow-green with constant 0.4 blue (MATLAB summer)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.502, 0.400,
      1.000, 1.000, 1.000, 0.400,
    ],
  },
  {
    Name: "Fall",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Red → Yellow (MATLAB autumn/fall)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 1.000, 0.000, 0.000,
      1.000, 1.000, 1.000, 0.000,
    ],
  },
  {
    Name: "Winter",
    ColorSpace: "RGB",
    // Black (background stays unlit) → Blue → Green-blue (MATLAB winter)
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.000, 1.000,
      1.000, 0.000, 1.000, 0.500,
    ],
  },
  {
    Name: "Rainbow",
    ColorSpace: "RGB",
    // Black (background stays unlit) → blue → cyan → green → yellow → red
    RGBPoints: [
      0.000, 0.000, 0.000, 0.000,
      0.150, 0.000, 0.000, 1.000,
      0.350, 0.000, 1.000, 1.000,
      0.550, 0.000, 1.000, 0.000,
      0.750, 1.000, 1.000, 0.000,
      1.000, 1.000, 0.000, 0.000,
    ],
  },
];

_CUSTOM_COLORMAP_DEFS.forEach((cm) => {
  try {
    utilities.colormap.registerColormap(cm);
  } catch { /* already registered — safe to ignore */ }
});

// ─── Available colormap lists ───────────────────────────────────────────────────
// Keys are the display names shown in the UI; values are the names used to look up
// the colormap (VTK.js built-in preset or Cornerstone custom registry).
// Object.values()[0] is used as the default, so list the desired default first.

/**
 * General-purpose colormaps for the Color LUT tool.
 * VTK.js built-ins are used where possible; custom-registered ones fill the rest.
 */
export const COLORMAPS = {
  "Hot Iron":       "Hot Iron",
  "PET":            "PET",
  "Hot Metal Blue": "Hot Metal Blue",
  "PET 20 Step":    "PET 20 Step",
  "Rainbow":        "Rainbow",
  "Spring":         "Spring",
  "Summer":         "Summer",
  "Fall":           "Fall",
  "Winter":         "Winter",
};

/**
 * Check if viewport is a StackViewport
 */
const isStackViewport = (viewport) => {
  return viewport && viewport.type === Enums.ViewportType.STACK;
};

/**
 * Check if viewport is a VolumeViewport (3D or orthographic)
 */
const isVolumeViewport = (viewport) => {
  return (
    viewport &&
    (viewport.type === Enums.ViewportType.VOLUME_3D ||
      viewport.type === Enums.ViewportType.ORTHOGRAPHIC)
  );
};

/**
 * Apply a colormap to a single viewport using Cornerstone3D native APIs.
 * Follows the OHIF setViewportColormap pattern:
 *   - StackViewport: viewport.setProperties({ colormap })
 *   - VolumeViewport: viewport.setProperties({ colormap }, volumeId)
 *
 * @param {object} viewport - Cornerstone3D viewport instance
 * @param {string} colormapName - VTK.js preset name (from COLORMAPS)
 * @param {object} options - { opacity, invert }
 * @returns {boolean} true if successfully applied
 */
export const applyColormapToViewport = (viewport, colormapName, options = {}) => {
  const { opacity = 1 } = options;

  const colormap = {
    name: colormapName,
  };

  try {
    if (isStackViewport(viewport)) {
      // Apply the colormap (StackViewport ignores opacity in colormap object)
      viewport.setProperties({ colormap });

      // Set opacity directly on the VTK actor since StackViewport.setColormapGPU
      // does not handle the colormap.opacity property
      const actorEntry = viewport.getDefaultActor?.();
      if (actorEntry?.actor) {
        const property = actorEntry.actor.getProperty?.();
        if (property?.setOpacity) {
          property.setOpacity(opacity);
        }
      }

      viewport.render();
      return true;
    }

    if (isVolumeViewport(viewport)) {
      // VolumeViewport handles OpacityMapping[] natively in BaseVolumeViewport
      const opacityMapping = [
        { value: 0, opacity },
        { value: 1, opacity },
      ];
      colormap.opacity = opacityMapping;

      const volumeId = viewport.getAllVolumeIds?.()?.[0] ?? viewport.getVolumeId?.();
      viewport.setProperties({ colormap }, volumeId);
      viewport.render();
      return true;
    }

    console.warn("Unsupported viewport type for colormap:", viewport.type);
    return false;
  } catch (error) {
    console.error("Error applying colormap to viewport:", error);
    return false;
  }
};

/**
 * Reset (remove) colormap from a single viewport.
 * Sets invert to false and resets properties to default grayscale rendering.
 *
 * @param {object} viewport - Cornerstone3D viewport instance
 * @returns {boolean} true if successfully reset
 */
export const resetColormapOnViewport = (viewport) => {
  try {
    if (isStackViewport(viewport)) {
      // Restore actor opacity to full before resetting properties
      const actorEntry = viewport.getDefaultActor?.();
      if (actorEntry?.actor) {
        const property = actorEntry.actor.getProperty?.();
        if (property?.setOpacity) {
          property.setOpacity(1);
        }
      }
      // resetProperties restores default rendering (removes colormap transfer function)
      viewport.resetProperties?.();
      viewport.render();
      return true;
    }

    if (isVolumeViewport(viewport)) {
      const volumeId = viewport.getAllVolumeIds?.()?.[0] ?? viewport.getVolumeId?.();
      viewport.resetProperties?.(volumeId);
      viewport.render();
      return true;
    }

    return false;
  } catch (error) {
    console.error("Error resetting colormap on viewport:", error);
    return false;
  }
};

/**
 * Apply colormap to all provided viewports.
 *
 * @param {Array} viewports - Array of Cornerstone3D viewport instances
 * @param {string} colormapName - VTK.js preset name
 * @param {object} options - { opacity }
 * @returns {number} count of successfully processed viewports
 */
export const applyColormapToViewports = (viewports, colormapName, options = {}) => {
  let processedCount = 0;

  for (const viewport of viewports) {
    const success = applyColormapToViewport(viewport, colormapName, options);
    if (success) processedCount++;
  }

  return processedCount;
};

/**
 * Reset colormap on all provided viewports.
 *
 * @param {Array} viewports - Array of Cornerstone3D viewport instances
 * @returns {number} count of successfully reset viewports
 */
export const resetColormapOnViewports = (viewports) => {
  let resetCount = 0;

  for (const viewport of viewports) {
    const success = resetColormapOnViewport(viewport);
    if (success) resetCount++;
  }

  return resetCount;
};

/**
 * Register a custom colormap with Cornerstone3D.
 * Use this for colormaps not provided by VTK.js built-in presets.
 *
 * @param {string} name - Unique colormap name
 * @param {string} colorSpace - Color space (e.g. "RGB", "Diverging")
 * @param {number[]} rgbPoints - Flat array of [x, r, g, b, ...] control points
 *                                x is normalized 0-1, r/g/b are 0-1
 */
export const registerCustomColormap = (name, colorSpace, rgbPoints) => {
  utilities.colormap.registerColormap({
    Name: name,
    ColorSpace: colorSpace,
    RGBPoints: rgbPoints,
  });
};

/**
 * Get list of registered custom colormap names.
 * @returns {string[]}
 */
export const getRegisteredColormapNames = () => {
  return utilities.colormap.getColormapNames?.() ?? [];
};

// ─── PET/CT Fusion Service ──────────────────────────────────────────────────

const PET_FUSION_ACTOR_UID = "pet-fusion-overlay";

/**
 * Recommended colormaps for PET fusion.
 * Hot Iron is first so it becomes the default when the overlay opens.
 */
export const PET_COLORMAPS = {
  "Hot Iron":       "Hot Iron",
  "PET":            "PET",
  "Hot Metal Blue": "Hot Metal Blue",
  "PET 20 Step":    "PET 20 Step",
  "Rainbow":        "Rainbow",
  "Spring":         "Spring",
  "Summer":         "Summer",
  "Fall":           "Fall",
  "Winter":         "Winter",
};

/**
 * Apply PET/CT fusion by overlaying a PET series image onto the active CT viewport.
 *
 * Workflow:
 *   1. Load the PET DICOM image for the current slice
 *   2. Colorize grayscale PET pixel data using the selected VTK.js colormap
 *   3. Create an RGBA VTK image data with the colorized pixels
 *   4. Add as an overlay actor with controllable opacity
 *
 * @param {object} viewport - The CT StackViewport to overlay PET onto
 * @param {string[]} petImageIds - Array of PET series imageIds (DICOM URIs)
 * @param {string} colormapName - VTK.js colormap preset name
 * @param {number} opacity - Overlay opacity 0-1
 * @returns {boolean} true if fusion was applied successfully
 */
export const applyPETFusion = async (viewport, petImageIds, colormapName, opacity = 0.5) => {
  try {
    if (!viewport || !petImageIds || petImageIds.length === 0) {
      console.warn("[PET Fusion] Missing viewport or PET imageIds");
      return false;
    }

    // Get the current slice index from the CT viewport
    const currentIndex = viewport.getCurrentImageIdIndex?.() || 0;
    const safeIndex = Math.min(currentIndex, petImageIds.length - 1);
    const petImageId = petImageIds[safeIndex];

    if (!petImageId) {
      console.warn("[PET Fusion] No PET image at index", safeIndex);
      return false;
    }

    // Load the PET DICOM image
    const petImage = await loadAndCacheImage(petImageId);
    if (!petImage) {
      console.warn("[PET Fusion] Failed to load PET image");
      return false;
    }

    // Get base CT image data for spatial reference
    const ctImageData = viewport.getImageData();
    if (!ctImageData) {
      console.warn("[PET Fusion] No CT image data on viewport");
      return false;
    }

    // Get PET pixel data (grayscale, 16-bit signed/unsigned)
    const petPixelData = petImage.getPixelData();
    const width = petImage.columns || petImage.width;
    const height = petImage.rows || petImage.height;
    const numPixels = width * height;

    // Determine PET value range for normalization
    let minVal = Infinity;
    let maxVal = -Infinity;
    for (let i = 0; i < petPixelData.length; i++) {
      if (petPixelData[i] < minVal) minVal = petPixelData[i];
      if (petPixelData[i] > maxVal) maxVal = petPixelData[i];
    }
    const range = maxVal - minVal || 1;

    // Build VTK color transfer function from the selected colormap
    const colormapPreset = vtkColorMaps.getPresetByName(colormapName);
    const cfun = vtkColorTransferFunction.newInstance();
    if (colormapPreset) {
      cfun.applyColorMap(colormapPreset);
    } else {
      // Fallback: use Cornerstone's registry
      const csColormap = utilities.colormap.getColormap?.(colormapName);
      if (csColormap) {
        cfun.applyColorMap(csColormap);
      } else {
        // Default hot-like fallback
        cfun.addRGBPoint(0.0, 0, 0, 0);
        cfun.addRGBPoint(0.25, 0.5, 0, 0);
        cfun.addRGBPoint(0.5, 1, 0.5, 0);
        cfun.addRGBPoint(0.75, 1, 1, 0);
        cfun.addRGBPoint(1.0, 1, 1, 1);
      }
    }
    cfun.setMappingRange(0, 1);

    // Colorize PET pixels into RGBA buffer
    const rgbaData = new Uint8Array(numPixels * 4);
    const rgb = [0, 0, 0];

    for (let i = 0; i < numPixels; i++) {
      const normalized = (petPixelData[i] - minVal) / range; // 0-1
      cfun.getColor(normalized, rgb);

      const offset = i * 4;
      rgbaData[offset] = Math.round(rgb[0] * 255);     // R
      rgbaData[offset + 1] = Math.round(rgb[1] * 255); // G
      rgbaData[offset + 2] = Math.round(rgb[2] * 255); // B
      // Bake both PET intensity AND user opacity into per-pixel alpha.
      // StackViewport GPU pipeline ignores actor-level opacity for overlay actors,
      // so we must encode it here.
      rgbaData[offset + 3] = Math.round(normalized * opacity * 255); // A
    }

    // OLD: placed the overlay using the overlay series' OWN patient-space geometry.
    // In a 2D StackViewport this renders the actor edge-on (a thin strip) whenever the
    // overlay plane isn't coplanar with the displayed base slice.
    //   const petMetadata = viewport.getImageDataMetadata?.(petImage);
    //   const petOrigin = petMetadata?.origin || ctImageData.origin || [0, 0, 0];
    //   const petDirection = petMetadata?.direction || ctImageData.direction;
    //   const petDimensions = [width, height, 1];
    //   const spacing = petMetadata?.spacing || ctImageData.spacing;

    // NEW: a StackViewport shows a single 2D plane, so the overlay must be placed COPLANAR
    // with the base image to be visible. Pin the overlay to the base image's plane
    // (origin + direction) and stretch it to exactly cover the base image's footprint.
    const baseDims = ctImageData.dimensions || [width, height, 1];
    const baseSpacing = ctImageData.spacing || [1, 1, 1];
    const petOrigin = ctImageData.origin || [0, 0, 0];
    const petDirection = ctImageData.direction;
    const petDimensions = [width, height, 1];

    // spacing(overlay) = base physical extent / overlay pixel dimensions
    const spacing = [
      ((baseDims[0] || width) * (baseSpacing[0] || 1)) / width,
      ((baseDims[1] || height) * (baseSpacing[1] || 1)) / height,
      baseSpacing[2] || 1,
    ];

    // Create VTK image data for the RGBA overlay
    const { default: vtkImageDataFactory } = await import(
      "@kitware/vtk.js/Common/DataModel/ImageData"
    );
    const { default: vtkDataArrayFactory } = await import(
      "@kitware/vtk.js/Common/Core/DataArray"
    );

    const scalarArray = vtkDataArrayFactory.newInstance({
      name: "PETFusionPixels",
      numberOfComponents: 4,
      values: rgbaData,
    });

    const fusionImageData = vtkImageDataFactory.newInstance();
    fusionImageData.setDimensions(petDimensions);
    fusionImageData.setSpacing(spacing);
    fusionImageData.setDirection(petDirection);
    fusionImageData.setOrigin(petOrigin);
    fusionImageData.getPointData().setScalars(scalarArray);

    // Try in-place mapper update when the overlay actor already exists.
    // This avoids the remove → add cycle on every scroll, preventing flicker.
    const existingEntry = viewport.getActor?.(PET_FUSION_ACTOR_UID);
    if (existingEntry?.actor) {
      const mapper = existingEntry.actor.getMapper?.();
      if (mapper?.setInputData) {
        mapper.setInputData(fusionImageData);
        mapper.modified?.();
        viewport.render();
        return true;
      }
      // In-place update unavailable — fall back to remove/re-add
      viewport.removeActors([PET_FUSION_ACTOR_UID]);
    }

    // First application: create actor and add as overlay
    const actor = viewport.createActorMapper(fusionImageData);
    if (actor) {
      const property = actor.getProperty?.();
      if (property?.setOpacity) {
        property.setOpacity(opacity);
      }
    }

    viewport.addActor({ uid: PET_FUSION_ACTOR_UID, actor, slabThickness: 1 });
    viewport.render();

    return true;
  } catch (error) {
    console.error("[PET Fusion] Error applying fusion:", error);
    return false;
  }
};

/**
 * Update the opacity of an existing PET fusion overlay without reloading.
 *
 * @param {object} viewport - The CT StackViewport with the PET overlay
 * @param {number} opacity - New opacity value 0-1
 * @returns {boolean} true if opacity was updated
 */
export const updatePETFusionOpacity = (viewport, opacity) => {
  try {
    const actorEntry = viewport.getActor?.(PET_FUSION_ACTOR_UID);
    if (!actorEntry?.actor) return false;

    const property = actorEntry.actor.getProperty?.();
    if (property?.setOpacity) {
      property.setOpacity(opacity);
      viewport.render();
      return true;
    }
    return false;
  } catch (error) {
    console.error("[PET Fusion] Error updating opacity:", error);
    return false;
  }
};

/**
 * Remove the PET fusion overlay from a viewport.
 *
 * @param {object} viewport - The CT StackViewport to remove PET overlay from
 * @returns {boolean} true if fusion was removed
 */
export const removePETFusion = (viewport) => {
  try {
    if (viewport.getActor?.(PET_FUSION_ACTOR_UID)) {
      viewport.removeActors([PET_FUSION_ACTOR_UID]);
      viewport.render();
      return true;
    }
    return false;
  } catch (error) {
    console.error("[PET Fusion] Error removing fusion:", error);
    return false;
  }
};

/**
 * Apply PET/CT fusion to all provided viewports.
 *
 * @param {Array} viewports - Array of CT StackViewport instances
 * @param {string[]} petImageIds - PET series image IDs
 * @param {string} colormapName - VTK.js colormap preset name
 * @param {number} opacity - Overlay opacity 0-1
 * @returns {number} count of viewports with fusion applied
 */
export const applyPETFusionToViewports = async (viewports, petImageIds, colormapName, opacity = 0.5) => {
  let count = 0;
  for (const viewport of viewports) {
    if (isStackViewport(viewport)) {
      const success = await applyPETFusion(viewport, petImageIds, colormapName, opacity);
      if (success) count++;
    }
  }
  return count;
};

/**
 * Remove PET fusion from all provided viewports.
 *
 * @param {Array} viewports - Array of viewport instances
 * @returns {number} count of viewports where fusion was removed
 */
export const removePETFusionFromViewports = (viewports) => {
  let count = 0;
  for (const viewport of viewports) {
    const success = removePETFusion(viewport);
    if (success) count++;
  }
  return count;
};

/**
 * Update PET fusion opacity on all provided viewports.
 *
 * @param {Array} viewports - Array of viewport instances
 * @param {number} opacity - New opacity 0-1
 * @returns {number} count of updated viewports
 */
export const updatePETFusionOpacityOnViewports = (viewports, opacity) => {
  let count = 0;
  for (const viewport of viewports) {
    const success = updatePETFusionOpacity(viewport, opacity);
    if (success) count++;
  }
  return count;
};
